import { createContext, useContext, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  FileText,
  GitCompareArrows,
  CalendarClock,
  CalendarDays,
  ListChecks,
  FileSpreadsheet,
  ShieldCheck,
  ShieldAlert,
  Vote,
  Landmark,
  Users,
  ScrollText,
  FlaskConical,
  Grid3x3,
  BookText,
  ClipboardCheck,
  Command as CommandIcon,
  LogOut,
  ChevronsUpDown,
  Menu,
} from "lucide-react";
import { useAuth } from "@/lib/auth/auth-provider";
import { useRole } from "@/lib/auth/use-role";
import { fetchTenantBySlug, type TenantSummary } from "@/lib/api/tenants";
import { countProvisionsByReview } from "@/lib/api/provisions";
import { CommandPalette } from "@/components/ui/command-palette";
import { BrandLockup } from "@/components/ui/primitives";
import { initials } from "@/lib/format";
import { cn } from "@/lib/utils";

const TenantContext = createContext<TenantSummary | null>(null);

export function useTenant(): TenantSummary {
  const t = useContext(TenantContext);
  if (!t) throw new Error("useTenant must be used inside <TenantLayout>");
  return t;
}

type Href =
  | "dashboard"
  | "funds"
  | "lps"
  | "documents"
  | "review-queue"
  | "mfn"
  | "obligations"
  | "calendar"
  | "requirements"
  | "filings"
  | "manual"
  | "brochure"
  | "ethics"
  | "contributions"
  | "restricted-list"
  | "audit"
  | "simulator"
  | "matrix";

interface NavItem {
  label: string;
  href: Href;
  icon: React.ComponentType<{ className?: string }>;
  /** Show the tenant's pending-provision count as a trailing pill, if any. */
  showPendingReviewBadge?: boolean;
}

const NAV_GROUPS: { heading: string; items: NavItem[] }[] = [
  {
    heading: "Overview",
    items: [{ label: "Dashboard", href: "dashboard", icon: LayoutDashboard }],
  },
  {
    heading: "Compliance",
    items: [
      { label: "Calendar", href: "calendar", icon: CalendarDays },
      { label: "Requirements", href: "requirements", icon: ListChecks },
      { label: "Regulatory filings", href: "filings", icon: FileSpreadsheet },
      { label: "Side-letter obligations", href: "obligations", icon: CalendarClock },
    ],
  },
  {
    heading: "Conduct",
    items: [
      { label: "Compliance manual", href: "manual", icon: BookText },
      { label: "Form ADV brochure", href: "brochure", icon: FileText },
      { label: "Code of ethics", href: "ethics", icon: ShieldCheck },
      { label: "Political contributions", href: "contributions", icon: Vote },
      { label: "Restricted list", href: "restricted-list", icon: ShieldAlert },
    ],
  },
  {
    heading: "Intelligence",
    items: [
      { label: "MFN triggers", href: "mfn", icon: GitCompareArrows },
      { label: "What-if simulator", href: "simulator", icon: FlaskConical },
      { label: "LP matrix", href: "matrix", icon: Grid3x3 },
    ],
  },
  {
    heading: "Operations",
    items: [
      { label: "Documents", href: "documents", icon: FileText },
      {
        label: "Review queue",
        href: "review-queue",
        icon: ClipboardCheck,
        showPendingReviewBadge: true,
      },
    ],
  },
  {
    heading: "Reference",
    items: [
      { label: "Funds", href: "funds", icon: Landmark },
      { label: "Limited partners", href: "lps", icon: Users },
      { label: "Audit trail", href: "audit", icon: ScrollText },
    ],
  },
];

export function TenantLayout({ children }: { children: ReactNode }) {
  const { slug } = useParams({ strict: false });
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);

  const slugStr = slug ?? "";
  const {
    data: tenant,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["tenant", slugStr],
    queryFn: () => fetchTenantBySlug(slugStr),
    enabled: !!slugStr,
  });

  // Called unconditionally (before the early returns below) so hook order
  // never changes across renders; only enabled once we have a tenant id.
  const { data: reviewCounts } = useQuery({
    queryKey: ["provision-counts", tenant?.id],
    queryFn: () => countProvisionsByReview(tenant?.id ?? ""),
    enabled: !!tenant?.id,
  });

  if (!slugStr) return <Navigate to="/select-tenant" replace />;

  if (isLoading) {
    return (
      <main className="argus-glow bg-neutral-975 flex min-h-dvh items-center justify-center text-sm text-neutral-400">
        Loading {slugStr}…
      </main>
    );
  }

  if (error || !tenant) {
    return (
      <main className="argus-glow bg-neutral-975 flex min-h-dvh flex-col items-center justify-center gap-3 px-6 text-neutral-100">
        <h1 className="text-xl font-semibold">Tenant not found</h1>
        <p className="text-sm text-neutral-400">
          You are not a member of <code className="text-neutral-200">{slugStr}</code>, or it does
          not exist.
        </p>
        <Link to="/select-tenant" className="text-xs text-neutral-300 hover:underline">
          ← Pick another tenant
        </Link>
      </main>
    );
  }

  return (
    <TenantContext.Provider value={tenant}>
      <CommandPalette slug={tenant.slug} />
      <div className="argus-glow bg-neutral-975 flex min-h-dvh text-neutral-100">
        {/* Mobile drawer backdrop */}
        {navOpen && (
          <button
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
            className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          />
        )}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-40 flex w-60 shrink-0 flex-col border-r border-neutral-900 bg-neutral-950 transition-transform duration-200 ease-out md:static md:z-auto md:translate-x-0 md:bg-neutral-950/40 print:hidden",
            navOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          {/* Product brand */}
          <div className="flex items-center border-b border-neutral-900 px-4 py-3.5">
            <Link
              to="/t/$slug/dashboard"
              params={{ slug: tenant.slug }}
              aria-label="Henosis Argus"
              onClick={() => setNavOpen(false)}
            >
              <BrandLockup size="sm" />
            </Link>
          </div>

          {/* Tenant switcher */}
          <Link
            to="/select-tenant"
            className="group flex items-center gap-2.5 border-b border-neutral-900 px-4 py-2.5 transition hover:bg-neutral-900/40"
          >
            <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-[11px] font-semibold text-neutral-300">
              {initials(tenant.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-neutral-100">{tenant.name}</div>
              <div className="truncate text-[11px] capitalize text-neutral-500">
                {tenant.plan.replace(/_/g, " ")} · {tenant.region.toUpperCase()}
              </div>
            </div>
            <ChevronsUpDown className="size-3.5 text-neutral-600 group-hover:text-neutral-400" />
          </Link>

          <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4 text-sm">
            {NAV_GROUPS.map((group) => (
              <div key={group.heading} className="space-y-0.5">
                <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-600">
                  {group.heading}
                </div>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.href}
                      to={`/t/$slug/${item.href}`}
                      params={{ slug: tenant.slug }}
                      onClick={() => setNavOpen(false)}
                      activeProps={{
                        className: "bg-brand-950/50 text-neutral-50 [&_svg]:text-brand-400",
                      }}
                      className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-neutral-400 transition hover:bg-neutral-900/70 hover:text-neutral-100"
                    >
                      <Icon className="size-4 shrink-0 text-neutral-500" />
                      {item.label}
                      {item.showPendingReviewBadge && reviewCounts && reviewCounts.pending > 0 ? (
                        <span className="ml-auto inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-amber-300">
                          {reviewCounts.pending}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>

          <div className="border-t border-neutral-900 p-3">
            <div className="flex items-center gap-2.5 rounded-md px-1 py-1">
              <div className="flex size-7 items-center justify-center rounded-full bg-neutral-800 text-[11px] font-medium text-neutral-300">
                {initials(user?.email ?? "?")}
              </div>
              <div className="min-w-0 flex-1 truncate text-[11px] text-neutral-500">
                {user?.email}
              </div>
              <button
                title="Sign out"
                onClick={() => {
                  void (async () => {
                    await signOut();
                    await navigate({ to: "/sign-in", replace: true });
                  })();
                }}
                className="rounded p-1 text-neutral-500 transition hover:bg-neutral-800 hover:text-neutral-200"
              >
                <LogOut className="size-3.5" />
              </button>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar onOpenNav={() => setNavOpen(true)} />
          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 md:px-8 md:py-7">
              {children}
            </div>
          </main>
        </div>
      </div>
    </TenantContext.Provider>
  );
}

function Topbar({ onOpenNav }: { onOpenNav: () => void }) {
  const openPalette = () => {
    document.dispatchEvent(
      new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }),
    );
  };
  return (
    <header className="bg-neutral-975/80 sticky top-0 z-20 flex h-12 items-center gap-3 border-b border-neutral-900 px-4 backdrop-blur md:px-8 print:hidden">
      <button
        onClick={onOpenNav}
        aria-label="Open navigation"
        className="mr-auto rounded-md border border-neutral-800 bg-neutral-900/60 p-1.5 text-neutral-400 transition hover:border-neutral-700 hover:text-neutral-200 md:hidden"
      >
        <Menu className="size-4" />
      </button>
      <button
        onClick={openPalette}
        className="ml-auto flex items-center gap-2 rounded-md border border-neutral-800 bg-neutral-900/60 px-2.5 py-1.5 text-xs text-neutral-400 transition hover:border-neutral-700 hover:text-neutral-200"
      >
        <CommandIcon className="size-3.5" />
        <span className="hidden sm:inline">Search & commands</span>
        <span className="sm:hidden">Search</span>
        <kbd className="ml-1 rounded border border-neutral-700 bg-neutral-800/70 px-1.5 py-0.5 text-[10px] text-neutral-400">
          ⌘K
        </kbd>
      </button>
    </header>
  );
}

// Gate write-action pages (create, edit) to owner/admin. DB-level RLS is the
// real enforcement (build doc §9.2); this is UX so non-write members get a
// clear message instead of a silent DB rejection.
export function RequireWrite({ children }: { children: ReactNode }) {
  const tenant = useTenant();
  const { canWrite, isLoading } = useRole(tenant.id);
  if (isLoading) {
    return <p className="text-sm text-neutral-500">Checking permissions…</p>;
  }
  if (!canWrite) {
    return (
      <div className="rounded-lg border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-300">
        You need an owner or admin role on this tenant to make changes here. Ask your tenant owner
        to upgrade your access.
      </div>
    );
  }
  return <>{children}</>;
}
